import React, { useMemo, useState, useEffect } from 'react';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import TripCard from '../components/TripCard';
import TripDetailDialog from '../components/TripDetailDialog';
import { useTrips } from '../context/TripsContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CITIES } from '../mock';
import { PawPrint, Home, MapPin, ArrowUpDown, Plus, Calendar, X, ArrowRightLeft, Search as SearchIcon, ShieldCheck, Users, Palmtree, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';

const FilterChip = ({ active, onClick, icon: Icon, children }) => (
  <button className={`chip ${active ? 'active' : ''}`} onClick={onClick}>
    <Icon size={16} />
    {children}
  </button>
);

const SearchPage = () => {
  const { trips, refreshTrips } = useTrips();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [originFilter, setOriginFilter] = useState('');
  const [destFilter, setDestFilter] = useState('');
  const [filters, setFilters] = useState({ pet: false, home: false, near: false });
  const [sort, setSort] = useState('date');
  const [sortOpen, setSortOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [searching, setSearching] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const PAGE_SIZE = 20;

  const doSearch = async () => {
    setSearching(true);
    setVisibleCount(PAGE_SIZE);  // reset to page 1 on every new search
    const params = {};
    if (originFilter.trim()) params.origin = originFilter.trim();
    if (destFilter.trim()) params.destination = destFilter.trim();
    if (dateFilter) {
      const [y, m, d] = dateFilter.split('-');
      params.date = `${d}/${m}/${y}`;
    }
    if (filters.pet) params.pet = true;
    if (filters.home) params.home = true;
    if (sort !== 'date') params.sort = sort;
    await refreshTrips(params);
    setSearching(false);
  };

  // Available cities from trips + mock CITIES list
  const availableCities = useMemo(() => {
    const set = new Set(CITIES);
    trips.forEach((t) => { if (t.origin) set.add(t.origin); if (t.destination) set.add(t.destination); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [trips]);

  const swap = () => {
    setOriginFilter(destFilter);
    setDestFilter(originFilter);
  };

  // Convert display date "DD/MM/YYYY" to ISO "YYYY-MM-DD"
  const toIso = (d) => {
    if (!d) return '';
    const parts = d.split('/');
    if (parts.length !== 3) return d;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const filtered = useMemo(() => {
    let list = trips.filter((t) => t.status !== 'cancelada');
    if (filters.pet) list = list.filter((t) => t.petFriendly);
    if (filters.home) list = list.filter((t) => t.homePickup);
    if (dateFilter) list = list.filter((t) => toIso(t.date) === dateFilter);
    if (originFilter.trim()) {
      const q = originFilter.trim().toLowerCase();
      list = list.filter((t) => t.origin.toLowerCase().includes(q));
    }
    if (destFilter.trim()) {
      const q = destFilter.trim().toLowerCase();
      list = list.filter((t) => t.destination.toLowerCase().includes(q));
    }
    if (sort === 'price') list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [trips, filters, originFilter, destFilter, sort, dateFilter]);

  // Reset pagination whenever the filtered result changes
  const filteredLength = filtered.length;
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [originFilter, destFilter, dateFilter, filters.pet, filters.home, sort]);

  const visibleTrips = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = filteredLength > visibleCount;

  return (
    <div className="mobile-shell">
      <Header />

      {/* Hero banner — edge to edge */}
      <div className="relative -mx-0 mb-4">
        <img
          src="/vip-hero-banner.png"
          alt="VIP Litoral — Sua carona, sua melhor escolha"
          className="w-full h-auto block"
          data-testid="hero-banner"
        />
      </div>

      <div className="px-5">
        {user?.role === 'motorista' && (
          <button
            onClick={() => navigate('/publicar')}
            className="btn-yellow w-full flex items-center justify-center gap-1.5 py-2.5 mb-3"
            title="Publicar viagem"
          >
            <Plus size={16} /> Publicar viagem
          </button>
        )}

        <datalist id="cities-list">
          {availableCities.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        {/* Search card (matches reference design) */}
        <div className="bg-white rounded-3xl p-3 shadow-lg border border-[var(--bj-border)]">
          {/* Origem + swap + Destino */}
          <div className="relative">
            {/* Origem row */}
            <div
              className="flex items-center gap-4 rounded-2xl px-4 py-4"
              style={{ background: 'linear-gradient(180deg, #EEF3FF 0%, #F5F8FF 100%)', border: '1px solid #DCE4F5' }}
            >
              <div
                className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center"
                style={{ background: '#E1E8FA', boxShadow: 'inset 0 0 0 1px #D0DAF3' }}
              >
                <MapPin size={28} className="text-[var(--bj-navy)]" fill="var(--bj-navy)" strokeWidth={0} />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7A9A] mb-0.5">De onde você sai?</label>
                <input
                  list="cities-list"
                  className="w-full bg-transparent text-[22px] font-extrabold text-[var(--bj-text)] focus:outline-none placeholder:text-[var(--bj-text)] tracking-tight leading-tight"
                  placeholder="Origem"
                  value={originFilter}
                  onChange={(e) => setOriginFilter(e.target.value)}
                  data-testid="search-origin"
                />
              </div>
              {originFilter ? (
                <button type="button" onClick={() => setOriginFilter('')} className="w-8 h-8 rounded-full hover:bg-[var(--bj-cream-2)] flex items-center justify-center shrink-0" title="Limpar">
                  <X size={16} />
                </button>
              ) : (
                <ChevronRight size={22} className="text-[var(--bj-muted)] shrink-0" strokeWidth={2.5} />
              )}
            </div>

            {/* Swap button - centered vertically between origin & destino */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <button
                type="button"
                onClick={swap}
                className="w-12 h-12 rounded-full bg-white flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                style={{ boxShadow: '0 4px 14px rgba(11,42,91,0.2), 0 0 0 4px white' }}
                title="Inverter origem e destino"
                data-testid="swap-btn"
              >
                <ArrowUpDown size={22} className="text-[var(--bj-navy)]" strokeWidth={3} />
              </button>
            </div>

            {/* Destino row */}
            <div
              className="flex items-center gap-4 rounded-2xl px-4 py-4 mt-2"
              style={{ background: 'linear-gradient(180deg, #FFF7DE 0%, #FFFAEE 100%)', border: '1px solid #F3E5B8' }}
            >
              <div
                className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center"
                style={{ background: '#FBEBB8', boxShadow: 'inset 0 0 0 1px #EFD98D' }}
              >
                <MapPin size={28} className="text-[var(--bj-yellow)]" fill="var(--bj-yellow)" strokeWidth={0} />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#8A6D0A] mb-0.5">Para onde você vai?</label>
                <input
                  list="cities-list"
                  className="w-full bg-transparent text-[22px] font-extrabold text-[var(--bj-text)] focus:outline-none placeholder:text-[var(--bj-text)] tracking-tight leading-tight"
                  placeholder="Destino"
                  value={destFilter}
                  onChange={(e) => setDestFilter(e.target.value)}
                  data-testid="search-destination"
                />
              </div>
              {destFilter ? (
                <button type="button" onClick={() => setDestFilter('')} className="w-8 h-8 rounded-full hover:bg-[var(--bj-cream-2)] flex items-center justify-center shrink-0" title="Limpar">
                  <X size={16} />
                </button>
              ) : (
                <ChevronRight size={22} className="text-[var(--bj-muted)] shrink-0" strokeWidth={2.5} />
              )}
            </div>
          </div>

          {/* Quando (date) row */}
          <div
            className="flex items-center gap-4 rounded-2xl px-4 py-4 mt-3 relative"
            style={{ background: 'linear-gradient(180deg, #EEF3FF 0%, #F5F8FF 100%)', border: '1px solid #DCE4F5' }}
          >
            <div
              className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center"
              style={{ background: '#E1E8FA', boxShadow: 'inset 0 0 0 1px #D0DAF3' }}
            >
              <Calendar size={26} className="text-[var(--bj-navy)]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7A9A] mb-0.5">Quando?</label>
              <div className="relative">
                <input
                  type="date"
                  className={`w-full bg-transparent text-[22px] font-extrabold focus:outline-none tracking-tight leading-tight [color-scheme:light] ${dateFilter ? 'text-[var(--bj-text)]' : 'text-transparent'}`}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  data-testid="search-date"
                />
                {!dateFilter && (
                  <div className="absolute inset-0 flex items-center pointer-events-none text-[22px] font-extrabold text-[var(--bj-text)]">
                    Selecione a data
                  </div>
                )}
              </div>
            </div>
            <ChevronRight size={22} className="text-[var(--bj-muted)] shrink-0" strokeWidth={2.5} />
            {dateFilter && (
              <button
                onClick={(e) => { e.stopPropagation(); setDateFilter(''); }}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-[var(--bj-border)] hover:border-[var(--bj-red)] hover:text-[var(--bj-red)] flex items-center justify-center"
                title="Limpar data"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Extra filter chips (inside the card) */}
          <div className="flex flex-wrap gap-2 mt-3 justify-center">
            <FilterChip active={filters.pet} onClick={() => setFilters({ ...filters, pet: !filters.pet })} icon={PawPrint}>Pet friendly</FilterChip>
            <FilterChip active={filters.home} onClick={() => setFilters({ ...filters, home: !filters.home })} icon={Home}>Busca em casa</FilterChip>
            <FilterChip active={filters.near} onClick={() => setFilters({ ...filters, near: !filters.near })} icon={MapPin}>Próximo a mim</FilterChip>
          </div>

          {/* Search button */}
          <button
            type="button"
            onClick={doSearch}
            disabled={searching}
            className="w-full mt-3 relative flex items-center justify-center gap-3 py-4 rounded-full font-extrabold text-white text-lg tracking-wide transition-all hover:scale-[1.01] active:scale-95 disabled:opacity-60"
            style={{
              background: 'linear-gradient(135deg, #2B5FCC 0%, #123B7A 55%, #0B2A5B 100%)',
              boxShadow: '0 8px 22px rgba(11,42,91,0.4)',
            }}
            data-testid="search-btn"
          >
            <span className="absolute left-3 w-11 h-11 rounded-full bg-white/15 flex items-center justify-center">
              <SearchIcon size={22} strokeWidth={3} className="text-white" />
            </span>
            <span className="pl-6 pr-6">{searching ? 'BUSCANDO…' : 'BUSCAR VIAGENS'}</span>
            <span className="absolute right-3 w-11 h-11 rounded-full bg-white flex items-center justify-center">
              <ArrowRight size={20} strokeWidth={3} className="text-[var(--bj-navy)]" />
            </span>
          </button>
        </div>

        {/* Trust badges */}
        <div className="mt-4 mb-3 bg-white rounded-2xl border border-[var(--bj-border)] px-2 py-3 grid grid-cols-3 gap-1 items-start">
          <div className="flex flex-col items-center gap-1 text-center px-1 border-r border-[var(--bj-border)]">
            <ShieldCheck size={22} className="text-[var(--bj-navy)]" strokeWidth={2.5} />
            <div className="text-[10px] font-extrabold uppercase leading-tight text-[var(--bj-text)]">Viagens<br />seguras</div>
          </div>
          <div className="flex flex-col items-center gap-1 text-center px-1 border-r border-[var(--bj-border)]">
            <Users size={22} className="text-[var(--bj-navy)]" strokeWidth={2.5} />
            <div className="text-[10px] font-extrabold uppercase leading-tight text-[var(--bj-text)]">Motoristas<br />verificados</div>
          </div>
          <div className="flex flex-col items-center gap-1 text-center px-1">
            <Palmtree size={22} className="text-[var(--bj-navy)]" strokeWidth={2.5} />
            <div className="text-[10px] font-extrabold uppercase leading-tight text-[var(--bj-text)]">Mais economia<br />para você</div>
          </div>
        </div>

        {/* Extra filter chips */}
        <div className="hidden">
          <FilterChip active={filters.pet} onClick={() => setFilters({ ...filters, pet: !filters.pet })} icon={PawPrint}>Pet friendly</FilterChip>
          <FilterChip active={filters.home} onClick={() => setFilters({ ...filters, home: !filters.home })} icon={Home}>Busca em casa</FilterChip>
          <FilterChip active={filters.near} onClick={() => setFilters({ ...filters, near: !filters.near })} icon={MapPin}>Próximo a mim</FilterChip>
        </div>

        <button
          type="button"
          onClick={doSearch}
          disabled={searching}
          className="hidden"
          aria-hidden="true"
        >
          {searching ? 'Buscando...' : 'Buscar viagens'}
        </button>

        <div className="flex items-center justify-between mt-6 mb-3">
          <div className="text-[var(--bj-text)] opacity-80">{filtered.length} viagens encontradas{hasMore ? ` · mostrando ${visibleCount}` : ''}</div>
          <div className="relative">
            <button onClick={() => setSortOpen((v) => !v)} className="font-bold text-[var(--bj-yellow-dark)] flex items-center gap-1">
              <ArrowUpDown size={16} /> Ordenar
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-8 bg-white rounded-2xl shadow-lg border border-[#E5E7EB] py-2 w-44 z-10">
                {[
                  { k: 'date', l: 'Data (padrão)' },
                  { k: 'price', l: 'Menor preço' },
                  { k: 'rating', l: 'Melhor avaliação' },
                ].map((o) => (
                  <button
                    key={o.k}
                    onClick={() => { setSort(o.k); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--bj-cream-2)] ${sort === o.k ? 'font-bold text-[var(--bj-navy)]' : 'text-[var(--bj-text)]'}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pb-6">
          {visibleTrips.map((t) => (
            <TripCard key={t.id} trip={t} onClick={() => setSelectedTrip(t)} />
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-[var(--bj-text)] opacity-70">
              Nenhuma viagem encontrada. Tente ajustar os filtros.
            </div>
          )}
          {hasMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
              className="w-full mt-3 py-3 rounded-full font-extrabold text-white tracking-wide transition-all hover:scale-[1.01] active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #2B5FCC 0%, #123B7A 55%, #0B2A5B 100%)',
                boxShadow: '0 4px 14px rgba(11,42,91,0.3)',
              }}
              data-testid="load-more-btn"
            >
              Ver mais {Math.min(PAGE_SIZE, filtered.length - visibleCount)} de {filtered.length - visibleCount} restantes
            </button>
          )}
        </div>
      </div>

      {selectedTrip && (
        <TripDetailDialog trip={selectedTrip} onClose={() => setSelectedTrip(null)} />
      )}

      <BottomNav />
    </div>
  );
};

export default SearchPage;
